import { Koi } from '@ksyos/koi';
import type { ISchemaContainer } from './schemaContainer';
import { SpirometerSoftwareNames } from '../enums/spirometerSoftwareNames';

export interface IServiceProviderEmailNotificationPreferencesValidated {
    sendNotificationEmployment: boolean;
    sendNotificationEmploymentAlt: boolean;
    sendNotificationPractitioner: boolean;
}

const serviceProviderEmailNotificationPreferences = Koi.object().keys({
    sendNotificationEmployment: Koi.boolean(),
    sendNotificationEmploymentAlt: Koi.boolean(),
    sendNotificationPractitioner: Koi.boolean(),
});

// eslint-disable-next-line @stylistic/max-len
export const serviceProviderEmailNotificationPreferencesSchema: ISchemaContainer<IServiceProviderEmailNotificationPreferencesValidated> =
    {
        schema: serviceProviderEmailNotificationPreferences.options({ convert: false, presence: 'required' }),
    };

interface IServiceProviderPersonalSettingsEditValidated {
    canSelectCareProviderAsServiceConsumer: boolean;
    isAnalyst: boolean;
    isAutoCreated?: boolean;
    isBiometrician: boolean;
    isGrader: boolean;
    isGraderSelector: boolean;
    isRequestCreator: boolean;
    isRequestRecipient: boolean;
    isServiceConsumerSelector: boolean;
    spirometerSoftwareName: SpirometerSoftwareNames | null;
    startDate: string;
    usingRestEcgMeter: boolean;
    usingSpirometer: boolean;
}

interface IServiceProviderEditValidated
    extends IServiceProviderEmailNotificationPreferencesValidated,
        IServiceProviderPersonalSettingsEditValidated {
    compensationPayoutGlaucoma: number | null;
    compensationPayoutProcedure: number | null;
    compensationPayoutReport: number | null;
    endDate: string | null;
    preferredOrganizations: string[];
    sendNotificationEmploymentAlt: boolean;
}

const serviceProviderPersonalSettingsEdit = Koi.object().keys({
    canSelectCareProviderAsServiceConsumer: Koi.when('isBiometrician', {
        is: false,
        otherwise: Koi.boolean(),
        then: Koi.valid(false),
    }),
    isAnalyst: Koi.boolean(),
    isAutoCreated: Koi.boolean().optional(),
    isBiometrician: Koi.when('isAnalyst', { is: true, otherwise: Koi.boolean(), then: Koi.valid(false) }),
    isGrader: Koi.when('isAnalyst', { is: true, otherwise: Koi.boolean(), then: Koi.valid(false) }),
    isGraderSelector: Koi.when('isBiometrician', { is: false, otherwise: Koi.boolean(), then: Koi.valid(false) }),
    isRequestCreator: Koi.boolean(),
    isRequestRecipient: Koi.boolean(),
    isServiceConsumerSelector: Koi.when('isBiometrician', {
        is: false,
        otherwise: Koi.boolean(),
        then: Koi.valid(false),
    }),
    spirometerSoftwareName: Koi.koi()
        .enum(SpirometerSoftwareNames)
        .when('usingSpirometer', { is: true, otherwise: Koi.allow(null), then: Koi.disallow(null) }),
    startDate: Koi.koi().date(),
    usingRestEcgMeter: Koi.boolean(),
    usingSpirometer: Koi.boolean(),
});

const serviceProviderEdit = Koi.object()
    .keys({
        compensationPayoutGlaucoma: Koi.number().min(0).precision(0).allow(null),
        compensationPayoutProcedure: Koi.number().min(0).precision(0).allow(null),
        compensationPayoutReport: Koi.number().min(0).precision(0).allow(null),
        endDate: Koi.koi().endDate().allow(null),
        preferredOrganizations: Koi.array().items(Koi.string().uuid()),
        sendNotificationEmploymentAlt: Koi.boolean(),
    })
    .concat(serviceProviderPersonalSettingsEdit)
    .concat(serviceProviderEmailNotificationPreferences);

export const serviceProviderEditSchema: ISchemaContainer<IServiceProviderEditValidated> = {
    schema: serviceProviderEdit.options({ convert: false, presence: 'required' }),
};

// eslint-disable-next-line @stylistic/max-len
export const serviceProviderPersonalSettingsEditSchema: ISchemaContainer<IServiceProviderPersonalSettingsEditValidated> =
    {
        schema: serviceProviderPersonalSettingsEdit.options({ convert: false, presence: 'required' }),
    };

export interface IServiceProviderAddValidated extends IServiceProviderEditValidated {
    employmentId: string;
    serviceId: string;
}

export const serviceProviderAddSchema: ISchemaContainer<IServiceProviderAddValidated> = {
    schema: serviceProviderEdit.concat(
        Koi.object({
            employmentId: Koi.string().uuid(),
            serviceId: Koi.string().uuid(),
        }).options({ convert: false, presence: 'required' })
    ),
};

interface IServiceProviderDeleteValidated {
    endDateIsCurrentDate: boolean;
}

const serviceProviderDelete = Koi.object({
    endDateIsCurrentDate: Koi.boolean(),
});

export const serviceProviderDeleteSchema: ISchemaContainer<IServiceProviderDeleteValidated> = {
    schema: serviceProviderDelete.options({ convert: false, presence: 'required' }),
};

export type IServiceProviderUpdateValidated =
    | IServiceProviderDeleteValidated
    | IServiceProviderEditValidated
    | IServiceProviderPersonalSettingsEditValidated;

export const serviceProviderUpdate = Koi.alternatives().try(
    serviceProviderEdit,
    serviceProviderPersonalSettingsEdit,
    serviceProviderDelete
);

export const serviceProviderUpdateSchema: ISchemaContainer<IServiceProviderUpdateValidated> = {
    schema: serviceProviderUpdate.options({ convert: false, presence: 'required' }),
};

export function isServiceProviderEditValidated(
    schemaInterface: IServiceProviderUpdateValidated
): schemaInterface is IServiceProviderEditValidated {
    return (
        (schemaInterface as IServiceProviderEditValidated).startDate !== undefined &&
        (schemaInterface as IServiceProviderEditValidated).isBiometrician !== undefined
    );
}

export function isServiceProviderDeleteValidated(
    schemaInterface: IServiceProviderUpdateValidated
): schemaInterface is IServiceProviderDeleteValidated {
    return (schemaInterface as IServiceProviderDeleteValidated).endDateIsCurrentDate;
}
